import { loadable } from '@change-corgi/core/react/loadable';

import type { AppRoute } from 'src/shared/routes';

import type { SubRouteConfig } from 'src/app/pages/petition/shared/routes';
import { createRoutes, handleRecruiter, Loading, petitionSlugCanonicalRedirect } from 'src/app/shared/routes';

import { resolvePetitionL10nForBotsFromSlugParam } from './shared/resolvePetitionL10nForBotsFromSlugParam';
import { shareUrlCanonicalRedirect } from './shared/shareUrlCanonicalRedirect';

function genSubRouteAdditionalConfig(additionalConfig: Omit<SubRouteConfig, 'routeType'>): SubRouteConfig {
	return {
		routeType: 'petitionSubRoute',
		...additionalConfig,
	};
}

const PETITION_DETAILS_BOT_SSR_CONFIG: AppRoute['ssr'] = {
	enabled: true,
	seo: true, // enabled so that twitter/FB can generate a preview
	resolveL10n: resolvePetitionL10nForBotsFromSlugParam,
	config: {
		disallowUserData: true,
	},
};

export const ROUTES: readonly AppRoute[] = [
	...createRoutes(
		{
			component: loadable(async () => import(/* webpackChunkName: "pages/petition_details" */ '../index'), {
				exportName: 'PetitionDetailsSubRouteRenderer',
				fallback: Loading,
			}),
			frame: true,
		},
		[
			{
				// Canonical petition route
				id: 'petition-details',
				path: '/p/:slug',
				beforeServerRender: async (context) => {
					await handleRecruiter(context, context.params.slug);
					await petitionSlugCanonicalRedirect(context);
				},
				ssr: {
					enabled: true,
					seo: true,
					resolveL10n: resolvePetitionL10nForBotsFromSlugParam,
					config: ({ loginState }) => {
						if (loginState === 'GUEST') {
							return {
								disallowUserData: true,
								cache: {
									enabled: true,
									lifetimeInSeconds: 1 * 60, // 1min
									queryParams: ['expired_session'],
								},
							};
						}
						// avoid caching for non-guest
						// mainly so that starters see their changes right away
						return {};
					},
				},
				additionalConfig: genSubRouteAdditionalConfig({
					component: loadable(async () => import(/* webpackChunkName: "pages/petition_details" */ '../index'), {
						exportName: 'PetitionDetails',
						fallback: Loading,
					}),
					gammaComponent: loadable(
						async () => import(/* webpackChunkName: "pages/petition_gamma" */ 'src/app/pages/petitionGamma/details'),
						{
							exportName: 'PetitionGammaDetails',
							fallback: Loading,
						},
					),
				}),
			},
			{
				// This route renders the same PetitionDetails component as the canonical route. The main component
				// includes the right meta tags when a petition is a victory.
				id: 'petition-details-victory',
				path: '/p/:slug/share_victory',
				beforeServerRender: shareUrlCanonicalRedirect,
				ssr: PETITION_DETAILS_BOT_SSR_CONFIG,
				additionalConfig: genSubRouteAdditionalConfig({
					component: loadable(async () => import(/* webpackChunkName: "pages/petition_details" */ '../index'), {
						exportName: 'PetitionDetails',
						fallback: Loading,
					}),
					gammaComponent: loadable(
						async () => import(/* webpackChunkName: "pages/petition_gamma" */ 'src/app/pages/petitionGamma/details'),
						{
							exportName: 'PetitionGammaDetails',
							fallback: Loading,
						},
					),
				}),
			},
			{
				id: 'petition-details-custom-preview-link-sfs',
				path: '/p/:slug/sfs/:shareChannel/:sharerUserId/:previewId',
				beforeServerRender: shareUrlCanonicalRedirect,
				ssr: PETITION_DETAILS_BOT_SSR_CONFIG,
				additionalConfig: genSubRouteAdditionalConfig({
					component: loadable(async () => import(/* webpackChunkName: "pages/petition_details" */ '../index'), {
						exportName: 'PetitionDetailsShareForStarters',
						fallback: Loading,
					}),
					gammaComponent: loadable(
						async () => import(/* webpackChunkName: "pages/petition_gamma" */ 'src/app/pages/petitionGamma/details'),
						{
							exportName: 'PetitionDetailsShareForStarters',
							fallback: Loading,
						},
					),
				}),
			},
			{
				id: 'petition-details-bandit-sfs',
				path: '/p/:slug/sfs/:shareChannel/:sharerUserId',
				beforeServerRender: shareUrlCanonicalRedirect,
				ssr: PETITION_DETAILS_BOT_SSR_CONFIG,
				additionalConfig: genSubRouteAdditionalConfig({
					component: loadable(async () => import(/* webpackChunkName: "pages/petition_details" */ '../index'), {
						exportName: 'PetitionDetailsShareForStarters',
						fallback: Loading,
					}),
					gammaComponent: loadable(
						async () => import(/* webpackChunkName: "pages/petition_gamma" */ 'src/app/pages/petitionGamma/details'),
						{
							exportName: 'PetitionDetailsShareForStarters',
							fallback: Loading,
						},
					),
				}),
			},
			{
				id: 'petition-details-bandit-nftexp',
				path: '/p/:slug/nftexp/:experimentName/:experimentVariantName/:sharerUserId',
				beforeServerRender: shareUrlCanonicalRedirect,
				ssr: PETITION_DETAILS_BOT_SSR_CONFIG,
				additionalConfig: genSubRouteAdditionalConfig({
					component: loadable(async () => import(/* webpackChunkName: "pages/petition_details" */ '../index'), {
						exportName: 'PetitionDetailsFacebookBandit',
						fallback: Loading,
					}),
					gammaComponent: loadable(
						async () => import(/* webpackChunkName: "pages/petition_gamma" */ 'src/app/pages/petitionGamma/details'),
						{
							exportName: 'PetitionDetailsFacebookBandit',
							fallback: Loading,
						},
					),
				}),
			},
			{
				id: 'petition-details-experiment-fbogexp',
				path: '/p/:slug/fbogexp/:experimentName/:experimentVariantName',
				beforeServerRender: shareUrlCanonicalRedirect,
				ssr: PETITION_DETAILS_BOT_SSR_CONFIG,
				additionalConfig: genSubRouteAdditionalConfig({
					component: loadable(async () => import(/* webpackChunkName: "pages/petition_details" */ '../index'), {
						exportName: 'PetitionDetailsFacebookExperiment',
						fallback: Loading,
					}),
					gammaComponent: loadable(
						async () => import(/* webpackChunkName: "pages/petition_gamma" */ 'src/app/pages/petitionGamma/details'),
						{
							exportName: 'PetitionDetailsFacebookExperiment',
							fallback: Loading,
						},
					),
				}),
			},
			{
				id: 'petition-details-bandit-fbog',
				path: '/p/:slug/fbog/:sharerUserId',
				beforeServerRender: shareUrlCanonicalRedirect,
				ssr: PETITION_DETAILS_BOT_SSR_CONFIG,
				additionalConfig: genSubRouteAdditionalConfig({
					component: loadable(async () => import(/* webpackChunkName: "pages/petition_details" */ '../index'), {
						exportName: 'PetitionDetailsFacebook',
						fallback: Loading,
					}),
					gammaComponent: loadable(
						async () => import(/* webpackChunkName: "pages/petition_gamma" */ 'src/app/pages/petitionGamma/details'),
						{
							exportName: 'PetitionDetailsFacebook',
							fallback: Loading,
						},
					),
				}),
			},
			{
				id: 'petition-details-bandit-exp',
				path: '/p/:slug/exp/:shareType/:experimentName/:experimentVariantName/:sharerUserId',
				beforeServerRender: shareUrlCanonicalRedirect,
				ssr: PETITION_DETAILS_BOT_SSR_CONFIG,
				additionalConfig: genSubRouteAdditionalConfig({
					component: loadable(async () => import(/* webpackChunkName: "pages/petition_details" */ '../index'), {
						exportName: 'PetitionDetailsBandit',
						fallback: Loading,
					}),
					gammaComponent: loadable(
						async () => import(/* webpackChunkName: "pages/petition_gamma" */ 'src/app/pages/petitionGamma/details'),
						{
							exportName: 'PetitionDetailsBandit',
							fallback: Loading,
						},
					),
				}),
			},
			{
				id: 'petition-details-bandit-exp-v2',
				path: '/p/:slug/exp/v2/:shareType/:experimentName/:experimentVariantName/:sharerUserId',
				beforeServerRender: shareUrlCanonicalRedirect,
				ssr: PETITION_DETAILS_BOT_SSR_CONFIG,
				additionalConfig: genSubRouteAdditionalConfig({
					component: loadable(async () => import(/* webpackChunkName: "pages/petition_details" */ '../index'), {
						exportName: 'PetitionDetailsBanditV2',
						fallback: Loading,
					}),
					gammaComponent: loadable(
						async () => import(/* webpackChunkName: "pages/petition_gamma" */ 'src/app/pages/petitionGamma/details'),
						{
							exportName: 'PetitionDetailsBanditV2',
							fallback: Loading,
						},
					),
				}),
			},
		],
	),
];
